import { motion } from "framer-motion";
import { Wine, Clock, Users, Sparkles, Heart, Crown, Gem } from "lucide-react";

const packages = [
  {
    id: "patch_class",
    name: "Patch Class",
    price: 125,
    icon: Heart,
    color: "rose",
    features: [
      "Regular Hats",
      "Patches",
      "Wine & Lite Refreshments",
      "2-2.5 Hours Experience",
      "Guided Design Assistance",
    ],
  },
  {
    id: "embellishment_class",
    name: "Embellishment Class",
    price: 200,
    icon: Crown,
    color: "gold",
    popular: true,
    features: [
      "Everything in Patch Class",
      "Premium Hats by Glamour",
      "Luxury Hat Options",
      "Premium Design Assistance",
    ],
  },
];

const highlights = [
  { icon: Users, text: "10-15 Guests" },
  { icon: Clock, text: "2-2.5 Hours" },
  { icon: Wine, text: "Wine & Cocktails Included" },
  { icon: Gem, text: "Hot Glue & Embellishments Provided" },
];

const GirlsNightSection = ({ onBookClick }) => {
  return (
    <section id="girls-night" data-testid="girls-night-section" className="py-20 md:py-32 px-4 md:px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16 md:mb-24"
        >
          <Wine className="w-8 h-8 text-rose mx-auto mb-4" />
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-2">
            Girls Night
          </h2>
          <p className="font-accent text-3xl md:text-4xl text-gold mb-6">
            Hat Making & Sip Experience
          </p>
          <p className="text-zinc-400 text-lg max-w-3xl mx-auto leading-relaxed">
            Grab your girls and design a custom statement hat while enjoying wine, cocktails, and good vibes. 
            A fun, stylish, guided experience you don&apos;t want to miss.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              data-testid={`package-${pkg.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`pricing-card ${pkg.popular ? 'vip' : ''} p-8 relative overflow-hidden`}
            >
              {/* Popular badge */}
              {pkg.popular && (
                <div className="absolute top-4 right-4">
                  <span className="bg-gold text-black text-xs font-bold px-3 py-1 uppercase tracking-wider">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Icon */}
              <div className={`w-14 h-14 mb-6 flex items-center justify-center ${
                pkg.color === 'gold' ? 'bg-gold/20' : 'bg-rose/20'
              }`}>
                <pkg.icon className={`w-7 h-7 ${
                  pkg.color === 'gold' ? 'text-gold' : 'text-rose'
                }`} />
              </div>

              {/* Name & Price */}
              <h3 className="font-heading text-2xl mb-2">{pkg.name}</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className={`text-4xl font-bold ${
                  pkg.color === 'gold' ? 'text-gold' : 'text-rose'
                }`}>
                  ${pkg.price}
                </span>
                <span className="text-zinc-500">/ person</span>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-zinc-300">
                    <Heart className={`w-4 h-4 flex-shrink-0 ${
                      pkg.color === 'gold' ? 'text-gold' : 'text-rose'
                    }`} fill="currentColor" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                data-testid={`book-${pkg.id}-btn`}
                onClick={() => onBookClick(pkg.id)}
                className={`w-full ${pkg.popular ? 'btn-gold' : 'btn-primary'}`}
              >
                Book Now
              </button>
            </motion.div>
          ))}
        </div>

        {/* Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-6 md:gap-12 mb-12"
        >
          {highlights.map((item, index) => (
            <div 
              key={index} 
              className="flex items-center gap-3 text-zinc-400"
            >
              <item.icon className="w-5 h-5 text-rose" />
              <span className="text-sm uppercase tracking-wider">{item.text}</span>
            </div>
          ))}
        </motion.div>

        {/* Payment Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto"
        >
          <div className="glass-card p-6 text-center">
            <h4 className="font-heading text-xl mb-6 text-rose">Payment Options</h4>
            
            {/* QR Codes */}
            <div className="flex flex-col md:flex-row justify-center gap-8 mb-6">
              <div className="flex flex-col items-center">
                <p className="text-gold font-medium mb-2">CashApp</p>
                <img 
                  src="https://customer-assets.emergentagent.com/job_threaded-events/artifacts/m30jrqri_Screenshot%202026-03-07%20at%208.39.54%E2%80%AFPM.png" 
                  alt="CashApp QR Code - $dainaLaneeBrand"
                  className="w-40 h-40 object-contain rounded-lg"
                />
                <p className="text-white font-medium mt-2">$dainaLaneeBrand</p>
              </div>
              <div className="flex flex-col items-center">
                <p className="text-gold font-medium mb-2">Zelle</p>
                <img 
                  src="https://customer-assets.emergentagent.com/job_threaded-events/artifacts/oil4vmnz_Croped%20Payment%20Zelle%20QR.png" 
                  alt="Zelle QR Code - dailightdg@gmail.com"
                  className="w-40 h-40 object-contain rounded-lg"
                />
                <p className="text-white font-medium mt-2">dailightdg@gmail.com</p>
              </div>
            </div>
            
            <p className="text-sm text-zinc-500">
              Need help? Call <a href="tel:7085390789" className="text-rose hover:text-rose-light">708-539-0789</a>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GirlsNightSection;
